import {
  useRef,
  useState,
  useEffect,
} from 'react';
import type {
  Ref,
  RefObject,
  ReactNode,
  MouseEvent,
} from 'react';
import {
  createPortal,
} from 'react-dom';
import classNames from 'classnames';
import useClickOutside from '@/hooks/useClickOutside';
import Button from '@/ui/components/Button';
import Text from '@/ui/components/Text';
import {
  CloseIcon,
} from '@/ui/icons';

import styles from './Modal.module.css';

type ModalProps = {
  ref?: Ref<HTMLFormElement>;
  title?: string;
  isOpen?: boolean;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  closable?: boolean;
  cancelText?: string;
  confirmText?: string;
  portal?: HTMLDivElement,
  dropdown?: boolean;
  allowScrolling?: boolean;
  children?: ReactNode;
  onClose?: () => void;
  onCancel?: () => void;
  onConfirm?: () => Promise<void>;
};

type Listener = ((this: Window, ev: KeyboardEvent) => void);

const CLOSE_TIMEOUT = 180;
const DEFAULT_PORTAL = typeof document !== 'undefined' ? document.body : null;

const Modal = (props: ModalProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    ref,
    title,
    isOpen,
    children,
    className,
    overlayClassName,
    contentClassName,
    closable = false,
    cancelText = 'Cancel',
    confirmText = 'Save',
    portal = DEFAULT_PORTAL,
    dropdown = false,
    allowScrolling,
    onClose,
    onCancel,
    onConfirm,
  } = props;

  // ##########################################################################
  // STATE
  // ##########################################################################

  const modalRef = useRef(null);
  const propsRef = ref as RefObject<HTMLFormElement>;
  const forwardedRef = propsRef || modalRef;

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const modal = forwardedRef.current as HTMLDivElement;
  const isHidden = modal?.offsetParent === null;
  const withActions = !!(onCancel || onConfirm);

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleSubmit = async () => {
    if (
      !onConfirm
    ) return;

    try {
      setLoading(true);
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = loading ? undefined : onClose;
  const handleCancel = loading ? undefined : onCancel;
  const handleClickOutside = open && dropdown ? handleClose : undefined;

  const handleOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    handleClose();
  };

  const blockScrolling = (isScrollable: boolean) => {
    if (
      isHidden ||
      allowScrolling
    ) return;

    document.body.classList.add('--modal');
    if (
      isScrollable
    ) document.body.classList.add('--scrollbar');
  };

  const unblockScrolling = () => {
    if (
      isHidden ||
      allowScrolling
    ) return;

    document.body.classList.remove('--modal');
    document.body.classList.remove('--scrollbar');
  };

  const handleUnmount = () => {
    setTimeout(() => {
      setOpen(false);
      unblockScrolling();
    }, CLOSE_TIMEOUT);
  };

  const handleKeyDown = (previousListener: Listener) => (
    (event: KeyboardEvent) => {
      const {
        key,
      } = event;

      if (key === 'Enter') {
        handleSubmit();
      } else if (key === 'Escape') {
        window.onkeydown = previousListener;
        onClose();
      }
    }
  );

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useClickOutside(forwardedRef, handleClickOutside);

  useEffect(() => {
    if (
      !isOpen
    ) return;

    return handleUnmount;
  }, [isOpen]);

  useEffect(() => {
    const upToDate = isOpen === open;

    if (
      !isOpen ||
      upToDate
    ) return;

    const isScrollable = window.innerWidth > document.body.clientWidth;

    setOpen(true);
    blockScrolling(isScrollable);
  }, [isOpen]);

  useEffect(() => {
    if (
      !isOpen
    ) return;

    window.onkeydown = handleKeyDown(window.onkeydown);
  }, [isOpen, handleSubmit]);

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const modalClassName = classNames({
    [styles.modal]: true,
    [styles.open]: isOpen,
    [styles.closed]: !isOpen,
    [styles.dropdown]: dropdown,
    [styles.actionable]: withActions,
    [className]: !!className,
  });

  const modalContentClassName = classNames({
    [styles.content]: true,
    [contentClassName]: !!contentClassName,
  });

  const modalOverlayClassName = classNames({
    [styles.overlay]: true,
    [overlayClassName]: !!overlayClassName,
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  if (
    !open ||
    !portal
  ) return null;

  return createPortal(
    <div
      ref={forwardedRef}
      className={modalClassName}
    >
      <div
        className={modalOverlayClassName}
        onMouseDown={handleOverlayClick}
      />

      <div className={modalContentClassName}>
        {title && (
          <div className={styles.title}>
            <Text
              kind="heading-2"
              color="neutral-600"
              weight="600"
              serif
            >
              {title}
            </Text>
          </div>
        )}

        <div className={styles.children}>
          {closable && (
            <div className={styles.close}>
              <Button
                kind="ghost"
                color="secondary"
                onClick={handleClose}
                centered
              >
                <CloseIcon />
              </Button>
            </div>
          )}

          {children}
        </div>

        <div className={styles.actions}>
          {onConfirm && (
            <div className={styles.confirm}>
              <Button
                type="submit"
                color="primary"
                roundness="round"
                onClick={handleSubmit}
                loading={loading}
                centered
              >
                <Text
                  kind="control"
                  size="regular"
                >
                  {confirmText}
                </Text>
              </Button>
            </div>
          )}

          {onCancel && (
            <div className={styles.cancel}>
              <Button
                color="secondary"
                roundness="round"
                onClick={handleCancel}
                disabled={loading}
                centered
              >
                <Text
                  kind="control"
                  size="regular"
                >
                  {cancelText}
                </Text>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>,
    portal,
  );
};

export default Modal;
export type { ModalProps };
